import React, { useState, useEffect, useRef } from 'react';
import { ChevronsRight, Phone, Sparkles, Search, MousePointer2 } from 'lucide-react';
import { LOGO_URL, services } from '../data/mock';
import AISearch from './AISearch';

// ============================================================
// Orbit config
// ============================================================
const ORBIT_RADIUS = 'clamp(88px, 15.5vw, 210px)';
const DOT_RADIUS = 'clamp(70px, 10.5vw, 140px)';
const FEATURED_CODES = ['BIS', 'WPC', 'TEC', 'CDSCO', 'FSSAI', 'PESO', 'DGFT', 'ARAI'];
const ORBIT_ITEMS = services.filter((service) => FEATURED_CODES.includes(service.code));

const Planet = ({ angle, radius, logo, code, tag, size, orbitDur, direction, isActive, onSelect }) => {
  const counterAnim = direction === 'cw' ? 'counter-cw' : 'counter-ccw';
  return (
    <div
      className="absolute top-1/2 left-1/2"
      style={{ transform: `rotate(${angle}deg) translate(${radius}) rotate(-${angle}deg)` }}
    >
      <div className={counterAnim} style={{ animationDuration: `${orbitDur}s` }}>
        <div
          className="group -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pop-in"
          style={{ width: size, height: size, animationDelay: `${angle * 4}ms` }}
          title={tag}
        >
          <div className="floaty w-full h-full" style={{ animationDelay: `${angle * 20}ms` }}>
            <button
              type="button"
              aria-label={`Select ${code}`}
              aria-pressed={isActive}
              onMouseEnter={onSelect}
              onFocus={onSelect}
              onClick={onSelect}
              className={`w-full h-full rounded-2xl bg-white border shadow-[0_8px_24px_-6px_rgba(196,30,58,0.15)] hover:shadow-[0_20px_40px_-6px_rgba(196,30,58,0.3)] hover:scale-110 transition-all flex items-center justify-center overflow-hidden relative ${
                isActive ? 'border-[#C41E3A] ring-4 ring-rose-100 scale-110' : 'border-neutral-200 hover:border-[#C41E3A]/60'
              }`}
            >
              <img
                src={logo}
                alt={code}
                className="w-[62%] h-[62%] object-contain"
                onError={(e) => {
                  const el = e.currentTarget;
                  el.style.display = 'none';
                  const parent = el.parentElement;
                  if (parent && !parent.querySelector('.fallback')) {
                    const span = document.createElement('span');
                    span.className = 'fallback text-[11px] font-bold text-[#C41E3A]';
                    span.textContent = code;
                    parent.appendChild(span);
                  }
                }}
              />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#C41E3A]/60" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Orbit showcase with mouse-parallax tilt
const OrbitShowcase = ({ activeService, onSelect }) => {
  const wrapRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMove = (e) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / rect.width;
    const dy = (e.clientY - cy) / rect.height;
    setTilt({ x: dx * 8, y: dy * -8 });
  };
  const handleLeave = () => setTilt({ x: 0, y: 0 });

  const canvasR = 300;
  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className="orbit-container relative w-full max-w-[570px] aspect-square mx-auto"
      style={{
        transform: `perspective(1000px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)`,
        transition: 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
        transformStyle: 'preserve-3d',
      }}
    >
      <div className="absolute inset-10 rounded-full bg-gradient-to-br from-rose-100/80 via-rose-50/50 to-amber-50/60 blur-3xl" />

      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 600 600">
        <defs>
          <radialGradient id="faderay" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C41E3A" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#C41E3A" stopOpacity="0" />
          </radialGradient>
        </defs>
        {ORBIT_ITEMS.map((_, i) => {
          const angle = (360 / ORBIT_ITEMS.length) * i;
          const rad = (angle * Math.PI) / 180;
          return (
            <line key={i} x1="300" y1="300"
              x2={300 + Math.cos(rad) * canvasR}
              y2={300 + Math.sin(rad) * canvasR}
              stroke="url(#faderay)" strokeWidth="0.8" />
          );
        })}
      </svg>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-rose-300/50"
        style={{ width: 'clamp(148px, 21vw, 285px)', height: 'clamp(148px, 21vw, 285px)' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-rose-200/70"
        style={{ width: 'clamp(204px, 31vw, 420px)', height: 'clamp(204px, 31vw, 420px)' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-rose-300/40"
        style={{ width: 'clamp(232px, 37vw, 500px)', height: 'clamp(232px, 37vw, 500px)' }} />

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="radar w-1/2 h-1 origin-left">
          <div className="w-full h-full bg-gradient-to-r from-[#C41E3A]/50 to-transparent rounded-full" />
        </div>
      </div>

      <div className="orbit-cw absolute inset-0" style={{ animationDuration: '72s' }}>
        {ORBIT_ITEMS.map((s, i) => (
          <Planet key={s.code} angle={((360 / ORBIT_ITEMS.length) * i + 270) % 360}
            radius={ORBIT_RADIUS} logo={s.logo} code={s.code} tag={s.tag}
            size="clamp(38px, 4.7vw, 64px)" orbitDur={72} direction="cw"
            isActive={activeService.code === s.code} onSelect={() => onSelect(s)} />
        ))}
      </div>

      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <span key={angle}
          className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full bg-[#C41E3A]/70 twinkle"
          style={{
            transform: `translate(-50%, -50%) rotate(${angle}deg) translate(${DOT_RADIUS})`,
            animationDelay: `${angle * 8}ms`,
          }} />
      ))}

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-rose-200/60 blur-xl" />
          <div className="soft-pulse relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-full bg-white shadow-2xl border-2 border-[#C41E3A]/25 flex items-center justify-center p-5 md:p-7">
            <img src={LOGO_URL} alt="Complise India" className="w-full h-full object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Rotating regulatory focus for the hero headline
const CYCLE_WORDS = ['REGULATORY', 'BIS · WPC', 'CDSCO · FSSAI', 'PESO · DGFT', 'CERTIFICATION'];
const WordCycle = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIdx((current) => (current + 1) % CYCLE_WORDS.length), 2400);
    return () => clearInterval(timer);
  }, []);

  return (
    <span
      key={idx}
      className="inline-block text-[#C41E3A]"
      style={{ animation: 'popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
    >
      {CYCLE_WORDS[idx]}
    </span>
  );
};

// Animated stat counter
const Counter = ({ value, suffix = '', duration = 1500 }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const step = (t) => {
          const p = Math.max(0, Math.min(1, (t - start) / duration));
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.round(value * eased));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [value, duration]);
  return <span ref={ref}>{n}{suffix}</span>;
};

// AI approval finder search
const ApprovalFinder = () => {
  const [q, setQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const suggestions = ['Bluetooth speaker', 'LED bulb', 'Medical thermometer', 'Power bank'];
  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) setSearchOpen(true);
  };

  return (
    <div className="relative w-full max-w-xl min-w-0">
      <div className="absolute -top-3 left-4 z-10">
        <span className="inline-flex items-center gap-1.5 bg-[#C41E3A] text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-md shadow-lg">
          <Sparkles className="w-3 h-3" /> AI Route Check
        </span>
      </div>
      <form onSubmit={submit} className="flex items-center w-full min-w-0 bg-white border-2 border-neutral-200 hover:border-[#C41E3A]/40 focus-within:border-[#C41E3A] rounded-2xl pl-4 sm:pl-5 pr-1 h-16 shadow-lg transition-all">
        <Search className="w-5 h-5 text-neutral-400 shrink-0 mr-3" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Enter your product — e.g. Bluetooth speaker"
          className="flex-1 bg-transparent outline-none text-sm sm:text-base text-neutral-900 placeholder:text-neutral-400 min-w-0"
        />
        <button type="submit" className="pill-btn h-12 pl-4 sm:pl-5 pr-1 rounded-xl bg-[#C41E3A] hover:bg-[#A81830] text-white text-sm font-semibold flex items-center gap-1.5 sm:gap-2 transition-colors shrink-0">
          Check
          <span className="w-9 h-9 rounded-lg bg-white flex items-center justify-center pill-arrow">
            <ChevronsRight className="w-3.5 h-3.5 text-[#C41E3A]" />
          </span>
        </button>
      </form>

      {/* Suggestions */}
      <div className="flex flex-wrap gap-2 mt-3 px-1">
        <span className="text-[11px] text-neutral-500">Try:</span>
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => { setQ(s); setSearchOpen(true); }}
            className="text-[11px] text-[#C41E3A] hover:text-neutral-900 underline underline-offset-2"
          >
            {s}
          </button>
        ))}
      </div>

      <AISearch open={searchOpen} onOpenChange={setSearchOpen} initialQuery={q} />
    </div>
  );
};

const Hero = () => {
  const [activeService, setActiveService] = useState(ORBIT_ITEMS[0]);

  return (
    <section className="relative overflow-hidden bg-[#FDFBF7] px-4 py-3 sm:px-6 sm:py-4 lg:px-10 lg:py-5">
      <div className="relative max-w-[1400px] mx-auto">
        <div className="relative grid min-w-0 lg:grid-cols-[minmax(0,0.96fr)_minmax(480px,1.04fr)] min-h-[620px] lg:min-h-[680px]">
          {/* Message and conversion content */}
          <div className="relative z-10 min-w-0 flex flex-col justify-center px-6 py-9 sm:px-9 sm:py-11 lg:px-10 xl:px-14 lg:py-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-px bg-[#C41E3A]" />
              <span className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-semibold">Complise India</span>
            </div>

            <h1 className="font-display text-neutral-900 text-[clamp(2.7rem,5.1vw,5rem)] font-extrabold leading-[0.94] tracking-[-0.05em] mb-5 max-w-[720px]">
              <span className="block">NEXT-GEN</span>
              <span className="block min-h-[1em]"><WordCycle /></span>
              <span className="block">COMPLIANCE</span>
            </h1>

            <p className="text-neutral-600 text-base md:text-lg leading-relaxed max-w-[620px] mb-6">
              Complise India coordinates BIS, WPC, TEC, CDSCO, FSSAI, BEE and legal compliances — from product classification and testing to authority filings, renewals and market entry — under one accountable team.
            </p>

            <div className="mb-7 pt-2 max-w-[620px]">
              <ApprovalFinder />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
              <a
                href="#contact"
                className="pill-btn group w-full sm:w-auto inline-flex items-center justify-between gap-3 rounded-full bg-[#C41E3A] hover:bg-[#D92545] text-white pl-6 pr-2 h-14 text-sm md:text-base font-semibold transition-colors shadow-[0_14px_36px_-12px_rgba(196,30,58,0.8)]"
              >
                Book Free Consult
                <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center pill-arrow">
                  <ChevronsRight className="w-4 h-4 text-[#C41E3A]" />
                </span>
              </a>
              <a href="tel:+918851590081" className="flex items-center gap-3 group">
                <span className="w-12 h-12 rounded-full border border-neutral-300 group-hover:border-[#C41E3A] group-hover:bg-[#C41E3A] flex items-center justify-center transition-colors">
                  <Phone className="w-4 h-4 text-neutral-800 group-hover:text-white transition-colors" />
                </span>
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">Speak to an advisor</div>
                  <div className="text-neutral-900 font-semibold mt-1">8851590081</div>
                </div>
              </a>
            </div>

            <div className="mt-8 grid grid-cols-3 max-w-xl border-t border-neutral-200 pt-5">
              <div className="pr-4">
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-neutral-900">
                  <Counter value={100} suffix="+" />
                </div>
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] text-neutral-500 mt-1.5">Approvals</div>
              </div>
              <div className="px-4 border-l border-neutral-200">
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-neutral-900">
                  <Counter value={30} suffix="+" />
                </div>
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] text-neutral-500 mt-1.5">Industries</div>
              </div>
              <div className="pl-4 border-l border-neutral-200">
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-[#C41E3A]">
                  <Counter value={14} />
                </div>
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] text-neutral-500 mt-1.5">Service routes</div>
              </div>
            </div>
          </div>

          {/* Contained visual system */}
          <div className="relative min-w-0 flex items-center p-3 pt-0 sm:p-5 sm:pt-0 lg:p-3 xl:p-4">
            <div className="relative w-full min-h-[430px] sm:min-h-[540px] lg:min-h-[650px] overflow-hidden rounded-[24px] sm:rounded-[30px] border border-neutral-200 bg-white/75 shadow-[0_24px_70px_-44px_rgba(196,30,58,0.35)] backdrop-blur-sm flex flex-col">
              <div className="relative z-20 flex items-start justify-between gap-4 px-6 pt-6 sm:px-7 sm:pt-7">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#C41E3A] font-semibold mb-2">Compliance ecosystem</div>
                  <h2 className="font-display text-xl sm:text-2xl font-bold text-neutral-900 leading-tight max-w-[280px]">One coordinated system for every approval.</h2>
                  <div className="mt-3 inline-flex max-w-[280px] items-center gap-2 rounded-full border border-rose-100 bg-rose-50/70 px-2.5 py-1.5" aria-live="polite">
                    <img src={activeService.logo} alt="" className="w-5 h-5 object-contain" />
                    <span className="text-[10px] font-bold text-[#C41E3A]">{activeService.code}</span>
                    <span className="truncate text-[10px] text-neutral-600">{activeService.name}</span>
                  </div>
                </div>
                <span className="mt-1 w-10 h-10 rounded-full border border-rose-200 bg-rose-50 flex items-center justify-center shrink-0" title="Hover or tap a regulator">
                  <MousePointer2 className="w-4 h-4 text-[#C41E3A]" />
                </span>
              </div>

              <div className="relative flex-1 flex items-center justify-center px-1 py-2 sm:px-4 sm:py-3">
                <OrbitShowcase activeService={activeService} onSelect={setActiveService} />
              </div>

              <div className="relative z-20 grid grid-cols-3 border-t border-neutral-200 bg-white/60">
                {['Advisory', 'Filing', 'Renewals'].map((item, index) => (
                  <div key={item} className={`px-3 py-4 sm:px-5 sm:py-5 text-center text-[10px] sm:text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold ${index > 0 ? 'border-l border-neutral-200' : ''}`}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
